/**
 * ADMIN PAGES - Registry-First Architecture + Control Plane v2
 * =============================================================
 * 
 * Affiche TOUTES les pages du système en utilisant le Page Registry
 * comme source de vérité unique.
 * 
 * CONTROL PLANE v2:
 * - Visibilité Retailer (publish/unpublish)
 * - Ordre d'affichage (display_order)
 * - Éditabilité HUB (hub_cms_enabled)
 * 
 * SOURCES FUSIONNÉES:
 * - Page Registry (structure, routes, types)
 * - pageConfig (metadata, labels)
 * - CMS (contenu uniquement)
 * - page_settings (contrôle admin)
 * 
 * SAFE MODE: Ne modifie aucune route, aucun backend
 */

import React, { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Edit, Trash2, Search, Eye, EyeOff, LayoutGrid, 
  ShieldAlert, Lock, AlertTriangle, ExternalLink, Filter,
  Briefcase, Settings, Zap, Clock, FileQuestion, Layers,
  CheckCircle2, Archive, Sparkles, CalendarClock, Percent,
  Star, TrendingUp, Heart, User, Home, GripVertical,
  Store, EyeOff as StoreHidden, ArrowUpDown, Blocks
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { DestructiveActionDialog } from '../../lib/DestructiveActionDialog';

// Import du Page Visibility Engine
import { 
  PAGE_VISIBILITY_CONFIG, 
  PAGE_STATUS, 
  BUSINESS_CATEGORY,
  getAdminPagesWithStatus,
  getStatusBadge,
  getCategoryBadge
} from '../../lib/pageVisibilityEngine';

import { isProtectedRoute, PAGE_TYPES } from '../../lib/pageRegistry';
import { useAllPageSettings } from '../../hooks/usePageSettings';
import { invalidateRetailerPagesCache } from '../../hooks/useRetailerPages';

// ============================================================================
// COMPOSANTS DE BADGES
// ============================================================================

const StatusBadge = ({ status }) => {
  const config = {
    [PAGE_STATUS.ACTIVE]: { 
      icon: CheckCircle2, 
      label: 'Actif', 
      className: 'bg-green-100 text-green-700 border-green-200' 
    },
    [PAGE_STATUS.RESERVED]: { 
      icon: Clock, 
      label: 'Réservé', 
      className: 'bg-amber-100 text-amber-700 border-amber-200' 
    },
    [PAGE_STATUS.ORPHAN]: { 
      icon: AlertTriangle, 
      label: 'Orphelin', 
      className: 'bg-orange-100 text-orange-700 border-orange-200' 
    },
    [PAGE_STATUS.LEGACY]: { 
      icon: Archive, 
      label: 'Legacy', 
      className: 'bg-gray-100 text-gray-500 border-gray-200' 
    },
    [PAGE_STATUS.CMS_ONLY]: { 
      icon: Layers, 
      label: 'CMS', 
      className: 'bg-blue-100 text-blue-700 border-blue-200' 
    },
    [PAGE_STATUS.MISSING]: { 
      icon: FileQuestion, 
      label: 'Manquant', 
      className: 'bg-red-100 text-red-700 border-red-200' 
    }
  };

  const cfg = config[status] || config[PAGE_STATUS.ACTIVE];
  const Icon = cfg.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border font-medium ${cfg.className}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
};

const CategoryBadge = ({ category }) => {
  const config = {
    [BUSINESS_CATEGORY.METIER]: { 
      icon: Briefcase, 
      label: 'Métier', 
      className: 'bg-purple-100 text-purple-700 border-purple-200' 
    },
    [BUSINESS_CATEGORY.NON_METIER]: { 
      icon: Settings, 
      label: 'Système', 
      className: 'bg-slate-100 text-slate-700 border-slate-200' 
    },
    [BUSINESS_CATEGORY.HYBRIDE]: { 
      icon: Zap, 
      label: 'Hybride', 
      className: 'bg-indigo-100 text-indigo-700 border-indigo-200' 
    }
  };

  const cfg = config[category] || config[BUSINESS_CATEGORY.NON_METIER];
  const Icon = cfg.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border font-medium ${cfg.className}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
};

const SourceBadge = ({ source }) => {
  const colors = {
    'Registry': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'CMS': 'bg-blue-100 text-blue-700 border-blue-200',
    'pageConfig': 'bg-violet-100 text-violet-700 border-violet-200'
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border font-medium ${colors[source] || 'bg-gray-100'}`}>
      {source}
    </span>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const AdminPages = () => {
  // États pour les pages CMS (éditables)
  const [cmsPages, setCmsPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Page Settings (Control Plane)
  const { 
    settingsMap, 
    loading: settingsLoading, 
    getSettingsForPage, 
    toggleRetailerVisibility: baseToggle,
    updatePageSettings: baseUpdate 
  } = useAllPageSettings();
  
  // Wrapper pour invalider le cache retailer après chaque modification
  const toggleRetailerVisibility = async (pageId) => {
    const result = await baseToggle(pageId);
    invalidateRetailerPagesCache(); // Invalider le cache côté retailer
    return result;
  };
  
  const updatePageSettings = async (pageId, updates) => {
    const result = await baseUpdate(pageId, updates);
    invalidateRetailerPagesCache(); // Invalider le cache côté retailer
    return result;
  };
  
  // Modal d'édition CMS
  const [showModal, setShowModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [lockdownWarning, setLockdownWarning] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    is_active: true,
    show_in_menu: true,
    menu_order: 1
  });

  // ============================================================================
  // FUSION REGISTRY + CMS
  // ============================================================================

  // Pages du Registry (source de vérité)
  const registryPages = useMemo(() => {
    return Object.values(PAGE_VISIBILITY_CONFIG).map(page => ({
      ...page,
      source: 'Registry',
      editable: false, // Pages registry ne sont pas éditables via CMS
      cmsPage: null
    }));
  }, []);

  // Fusion avec pages CMS + settings
  const allPages = useMemo(() => {
    const merged = [...registryPages];
    
    // Ajouter les pages CMS qui ne sont pas dans le registry
    cmsPages.forEach(cmsPage => {
      const existingIndex = merged.findIndex(p => p.route === cmsPage.slug);
      
      if (existingIndex >= 0) {
        // Page existe dans registry - enrichir avec données CMS
        merged[existingIndex] = {
          ...merged[existingIndex],
          cmsPage,
          editable: merged[existingIndex].status === PAGE_STATUS.CMS_ONLY,
          cmsActive: cmsPage.is_active,
          cmsInMenu: cmsPage.show_in_menu,
          cmsOrder: cmsPage.menu_order
        };
      } else {
        // Page CMS uniquement (non dans registry)
        merged.push({
          id: cmsPage.id,
          title: cmsPage.title,
          route: cmsPage.slug,
          type: 'CMS_CONTENT',
          category: BUSINESS_CATEGORY.METIER,
          status: PAGE_STATUS.CMS_ONLY,
          visibility: { admin: true, retailer: true, footer: false },
          source: 'CMS',
          editable: true,
          cmsPage,
          order: cmsPage.menu_order || 99
        });
      }
    });

    // Enrichir avec les settings (Control Plane)
    merged.forEach(page => {
      const settings = getSettingsForPage(page.id);
      page.settings = settings;
      page.isPublishedRetailer = settings.is_published_retailer !== false;
      page.displayOrder = settings.display_order || page.order || 99;
      page.hubCmsEnabled = settings.hub_cms_enabled !== false;
    });

    // Trier par display_order (settings) puis par order (registry)
    return merged.sort((a, b) => (a.displayOrder || 99) - (b.displayOrder || 99));
  }, [registryPages, cmsPages, settingsMap, getSettingsForPage]);

  // ============================================================================
  // STATISTIQUES
  // ============================================================================

  const stats = useMemo(() => ({
    total: allPages.length,
    active: allPages.filter(p => p.status === PAGE_STATUS.ACTIVE).length,
    reserved: allPages.filter(p => p.status === PAGE_STATUS.RESERVED).length,
    cmsOnly: allPages.filter(p => p.status === PAGE_STATUS.CMS_ONLY).length,
    missing: allPages.filter(p => p.status === PAGE_STATUS.MISSING).length,
    metier: allPages.filter(p => p.category === BUSINESS_CATEGORY.METIER).length,
    system: allPages.filter(p => p.category === BUSINESS_CATEGORY.NON_METIER).length,
    hybride: allPages.filter(p => p.category === BUSINESS_CATEGORY.HYBRIDE).length
  }), [allPages]);

  // ============================================================================
  // FILTRAGE
  // ============================================================================

  const filteredPages = useMemo(() => {
    return allPages.filter(page => {
      // Recherche texte
      const matchSearch = !searchQuery || 
        page.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        page.route?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Filtre par onglet
      let matchTab = true;
      if (activeTab === 'metier') {
        matchTab = page.category === BUSINESS_CATEGORY.METIER;
      } else if (activeTab === 'system') {
        matchTab = page.category === BUSINESS_CATEGORY.NON_METIER;
      } else if (activeTab === 'cms') {
        matchTab = page.editable || page.status === PAGE_STATUS.CMS_ONLY;
      }
      
      // Filtre par catégorie
      const matchCategory = categoryFilter === 'all' || page.category === categoryFilter;
      
      // Filtre par statut
      const matchStatus = statusFilter === 'all' || page.status === statusFilter;
      
      return matchSearch && matchTab && matchCategory && matchStatus;
    });
  }, [allPages, searchQuery, activeTab, categoryFilter, statusFilter]);

  // ============================================================================
  // CHARGEMENT CMS
  // ============================================================================

  useEffect(() => {
    loadCmsPages();
  }, []);

  const loadCmsPages = async () => {
    try {
      const response = await api.get('/pages');
      setCmsPages(response.data);
    } catch (error) {
      console.error('Error loading CMS pages:', error);
      toast.error('Erreur lors du chargement des pages CMS');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // HANDLERS MODAL CMS
  // ============================================================================

  const handleOpenModal = (page = null) => {
    if (page && page.cmsPage) {
      // Édition d'une page CMS existante
      setEditingPage(page.cmsPage);
      setFormData({
        title: page.cmsPage.title,
        slug: page.cmsPage.slug,
        is_active: page.cmsPage.is_active,
        show_in_menu: page.cmsPage.show_in_menu,
        menu_order: page.cmsPage.menu_order || 1
      });
      
      const { protected: isProtect, reason } = isProtectedRoute(page.cmsPage.slug);
      if (isProtect) {
        setLockdownWarning({
          type: 'warning',
          message: 'Cette page est sur une route système. Le slug ne peut pas être modifié.',
          reason
        });
      } else {
        setLockdownWarning(null);
      }
    } else {
      // Nouvelle page CMS
      setEditingPage(null);
      setFormData({
        title: '',
        slug: '',
        is_active: true,
        show_in_menu: true,
        menu_order: cmsPages.length + 1
      });
      setLockdownWarning(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPage(null);
    setLockdownWarning(null);
    setFormData({
      title: '',
      slug: '',
      is_active: true,
      show_in_menu: true,
      menu_order: 1
    });
  };

  const handleSlugChange = (newSlug) => {
    setFormData({ ...formData, slug: newSlug });
    
    const { protected: isProtect, reason } = isProtectedRoute(newSlug);
    if (isProtect && !editingPage) {
      setLockdownWarning({
        type: 'error',
        message: 'Cette route est protégée par le système. Vous ne pouvez pas créer de page CMS ici.',
        reason
      });
    } else if (isProtect && editingPage) {
      setLockdownWarning({
        type: 'warning',
        message: 'Le slug de cette page système ne peut pas être modifié.',
        reason
      });
    } else {
      setLockdownWarning(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const { protected: isProtect } = isProtectedRoute(formData.slug);
    if (isProtect && !editingPage) {
      toast.error('Impossible de créer une page sur une route système protégée');
      return;
    }
    
    try {
      const pageData = {
        ...formData,
        slug: formData.slug.startsWith('/') ? formData.slug : '/' + formData.slug
      };

      if (editingPage) {
        await api.put(`/pages/${editingPage.id}`, pageData);
        toast.success('Page modifiée avec succès');
      } else {
        await api.post('/pages', pageData);
        toast.success('Page créée avec succès');
      }
      
      loadCmsPages();
      handleCloseModal();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.lockdown) {
        toast.error(detail.errors?.[0] || 'Route protégée par le système');
      } else {
        toast.error(detail || 'Erreur lors de l\'enregistrement');
      }
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async () => {
    if (!deleteTarget?.cmsPage) return;
    try {
      await api.delete(`/pages/${deleteTarget.cmsPage.id}`);
      toast.success('Page supprimée');
      setDeleteTarget(null);
      loadCmsPages();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold" data-testid="admin-pages-title">
              Gestion des Pages
            </h1>
            <p className="text-muted-foreground mt-2">
              Vue complète de toutes les pages du système (Registry-driven)
            </p>
          </div>
          <Button onClick={() => handleOpenModal()} data-testid="create-page-button">
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle page CMS
          </Button>
        </div>

        {/* Stats Grid - Amélioré */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <div className="bg-white rounded-lg border p-4">
            <div className="text-xs text-muted-foreground mb-1">Total</div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-xs text-muted-foreground mb-1">Actives</div>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-xs text-muted-foreground mb-1">Réservées</div>
            <div className="text-2xl font-bold text-amber-600">{stats.reserved}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-xs text-muted-foreground mb-1">CMS</div>
            <div className="text-2xl font-bold text-blue-600">{stats.cmsOnly}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-xs text-muted-foreground mb-1">Manquantes</div>
            <div className="text-2xl font-bold text-red-600">{stats.missing}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-xs text-muted-foreground mb-1">Métier</div>
            <div className="text-2xl font-bold text-purple-600">{stats.metier}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-xs text-muted-foreground mb-1">Système</div>
            <div className="text-2xl font-bold text-slate-600">{stats.system}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-xs text-muted-foreground mb-1">Hybride</div>
            <div className="text-2xl font-bold text-indigo-600">{stats.hybride}</div>
          </div>
        </div>

        {/* Tabs et Filtres */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <TabsList>
              <TabsTrigger value="all">Toutes ({stats.total})</TabsTrigger>
              <TabsTrigger value="metier">Métier ({stats.metier})</TabsTrigger>
              <TabsTrigger value="system">Système ({stats.system})</TabsTrigger>
              <TabsTrigger value="cms">CMS Éditables ({stats.cmsOnly})</TabsTrigger>
            </TabsList>
            
            <div className="flex-1 flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher une page..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-pages-input"
                />
              </div>
              
              {/* Filtre Statut */}
              <select 
                className="px-3 py-2 border rounded-md text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Tous statuts</option>
                <option value={PAGE_STATUS.ACTIVE}>Actif</option>
                <option value={PAGE_STATUS.RESERVED}>Réservé</option>
                <option value={PAGE_STATUS.CMS_ONLY}>CMS</option>
                <option value={PAGE_STATUS.MISSING}>Manquant</option>
              </select>
            </div>
          </div>

          {/* Table Content */}
          <TabsContent value={activeTab} className="mt-4">
            <div className="bg-white rounded-lg border overflow-x-auto">
              {(loading || settingsLoading) ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-[60px]">
                        <ArrowUpDown className="h-4 w-4" title="Ordre" />
                      </TableHead>
                      <TableHead className="w-[220px]">Page</TableHead>
                      <TableHead className="w-[180px]">Route</TableHead>
                      <TableHead className="w-[80px]">Type</TableHead>
                      <TableHead className="w-[90px]">Catégorie</TableHead>
                      <TableHead className="w-[90px]">Statut</TableHead>
                      <TableHead className="w-[100px] text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Store className="h-4 w-4" />
                          <span>Retailer</span>
                        </div>
                      </TableHead>
                      <TableHead className="w-[80px] text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Blocks className="h-4 w-4" />
                          <span>Blocs</span>
                        </div>
                      </TableHead>
                      <TableHead className="text-right w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPages.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          Aucune page trouvée
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPages.map((page, index) => (
                        <TableRow 
                          key={page.id || page.route} 
                          data-testid={`page-row-${page.id}`}
                          className={`
                            ${page.status === PAGE_STATUS.MISSING ? 'bg-red-50' : ''}
                            ${page.status === PAGE_STATUS.RESERVED ? 'bg-amber-50' : ''}
                            ${!page.isPublishedRetailer && page.status === PAGE_STATUS.ACTIVE ? 'bg-gray-50' : ''}
                          `}
                        >
                          {/* Ordre */}
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
                              <Input
                                type="number"
                                value={page.displayOrder}
                                onChange={async (e) => {
                                  const newOrder = parseInt(e.target.value) || 99;
                                  try {
                                    await updatePageSettings(page.id, { display_order: newOrder });
                                  } catch (err) {
                                    toast.error('Erreur lors de la mise à jour');
                                  }
                                }}
                                className="w-14 h-7 text-xs text-center"
                                min="1"
                                max="999"
                              />
                            </div>
                          </TableCell>
                          
                          {/* Page Name */}
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{page.title}</span>
                              {page.type === 'HUB' && (
                                <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">HUB</span>
                              )}
                              {page.type === 'LISTING' && (
                                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">LIST</span>
                              )}
                            </div>
                          </TableCell>
                          
                          {/* Route */}
                          <TableCell>
                            <code className="text-xs px-2 py-1 rounded bg-gray-100 block truncate max-w-[170px]" title={page.route}>
                              {page.route}
                            </code>
                          </TableCell>
                          
                          {/* Type */}
                          <TableCell>
                            <span className="text-xs text-muted-foreground">
                              {page.type}
                            </span>
                          </TableCell>
                          
                          {/* Catégorie */}
                          <TableCell>
                            <CategoryBadge category={page.category} />
                          </TableCell>
                          
                          {/* Statut */}
                          <TableCell>
                            <StatusBadge status={page.status} />
                          </TableCell>
                          
                          {/* Visibilité Retailer Toggle */}
                          <TableCell className="text-center">
                            {page.status === PAGE_STATUS.ACTIVE || page.status === PAGE_STATUS.CMS_ONLY ? (
                              <Button
                                variant={page.isPublishedRetailer ? "default" : "outline"}
                                size="sm"
                                className={`h-7 px-2 ${page.isPublishedRetailer ? 'bg-green-600 hover:bg-green-700' : 'text-gray-500'}`}
                                onClick={() => toggleRetailerVisibility(page.id)}
                                title={page.isPublishedRetailer ? 'Masquer pour les magasins' : 'Publier pour les magasins'}
                              >
                                {page.isPublishedRetailer ? (
                                  <><Store className="h-3 w-3 mr-1" /> Visible</>
                                ) : (
                                  <><StoreHidden className="h-3 w-3 mr-1" /> Masqué</>
                                )}
                              </Button>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </TableCell>
                          
                          {/* Blocs CMS (pour HUB) */}
                          <TableCell className="text-center">
                            {(page.type === 'HUB' || page.cmsPage) ? (
                              <Link 
                                to={page.cmsPage 
                                  ? `/dashboard/admin/pages/${page.cmsPage.id}/blocks`
                                  : `/dashboard/admin/pages/hub-${page.id}/blocks`
                                }
                              >
                                <Button variant="outline" size="sm" className="h-7 px-2" title="Gérer les blocs CMS">
                                  <LayoutGrid className="h-3 w-3 mr-1" />
                                  {page.cmsPage ? 'Blocs' : 'CMS'}
                                </Button>
                              </Link>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </TableCell>
                          
                          {/* Actions */}
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {/* Lien externe vers la page */}
                              {page.status === PAGE_STATUS.ACTIVE && (
                                <Link to={page.route} target="_blank">
                                  <Button variant="ghost" size="sm" title="Voir la page">
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                </Link>
                              )}
                              
                              {/* Gérer les blocs (déplacé dans colonne Blocs) */}
                              
                              {/* Éditer (CMS uniquement) */}
                              {page.editable && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenModal(page)}
                                  title="Modifier"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                              
                              {/* Supprimer (CMS uniquement, non protégé) */}
                              {page.editable && page.cmsPage && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => page.cmsPage ? setDeleteTarget(page) : toast.error('Seules les pages CMS peuvent être supprimées')}
                                  title="Supprimer"
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Control Plane Summary */}
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-4 border border-emerald-200">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Control Plane v2 - Contrôles disponibles
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <Store className="h-4 w-4 text-green-600 mt-0.5" />
              <div>
                <span className="font-medium">Visibilité Retailer</span>
                <p className="text-xs text-muted-foreground">Publiez ou masquez les pages pour les magasins</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ArrowUpDown className="h-4 w-4 text-blue-600 mt-0.5" />
              <div>
                <span className="font-medium">Ordre d'affichage</span>
                <p className="text-xs text-muted-foreground">Contrôlez l'ordre des pages dans les menus</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <LayoutGrid className="h-4 w-4 text-purple-600 mt-0.5" />
              <div>
                <span className="font-medium">Blocs CMS (HUB)</span>
                <p className="text-xs text-muted-foreground">Personnalisez le contenu des pages HUB</p>
              </div>
            </div>
          </div>
        </div>

        {/* Légende */}
        <div className="bg-gray-50 rounded-lg p-4 text-sm">
          <h4 className="font-medium mb-2">Légende</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="font-medium text-xs block mb-1">Statuts:</span>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <StatusBadge status={PAGE_STATUS.ACTIVE} />
                  <span className="text-xs text-muted-foreground">Page fonctionnelle</span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={PAGE_STATUS.RESERVED} />
                  <span className="text-xs text-muted-foreground">Route réservée</span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={PAGE_STATUS.CMS_ONLY} />
                  <span className="text-xs text-muted-foreground">Contenu CMS</span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={PAGE_STATUS.MISSING} />
                  <span className="text-xs text-muted-foreground">Page manquante</span>
                </div>
              </div>
            </div>
            <div>
              <span className="font-medium text-xs block mb-1">Catégories:</span>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <CategoryBadge category={BUSINESS_CATEGORY.METIER} />
                  <span className="text-xs text-muted-foreground">Logique business</span>
                </div>
                <div className="flex items-center gap-2">
                  <CategoryBadge category={BUSINESS_CATEGORY.NON_METIER} />
                  <span className="text-xs text-muted-foreground">Pages système</span>
                </div>
                <div className="flex items-center gap-2">
                  <CategoryBadge category={BUSINESS_CATEGORY.HYBRIDE} />
                  <span className="text-xs text-muted-foreground">Structure + business</span>
                </div>
              </div>
            </div>
            <div>
              <span className="font-medium text-xs block mb-1">Sources:</span>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <SourceBadge source="Registry" />
                  <span className="text-xs text-muted-foreground">Source de vérité</span>
                </div>
                <div className="flex items-center gap-2">
                  <SourceBadge source="CMS" />
                  <span className="text-xs text-muted-foreground">Contenu éditable</span>
                </div>
              </div>
            </div>
            <div>
              <span className="font-medium text-xs block mb-1">Visibilité:</span>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-xs text-muted-foreground">Retailer</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500" />
                  <span className="text-xs text-muted-foreground">Admin</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-gray-500" />
                  <span className="text-xs text-muted-foreground">Footer</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Create/Edit CMS Page */}
        <Dialog open={showModal} onOpenChange={handleCloseModal}>
          <DialogContent className="sm:max-w-[600px]" data-testid="page-modal">
            <DialogHeader>
              <DialogTitle>
                {editingPage ? 'Modifier la page CMS' : 'Créer une nouvelle page CMS'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Lockdown Warning */}
              {lockdownWarning && (
                <div className={`flex items-start gap-3 p-4 rounded-lg border ${
                  lockdownWarning.type === 'error' 
                    ? 'bg-red-50 border-red-200 text-red-800' 
                    : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}>
                  {lockdownWarning.type === 'error' ? (
                    <ShieldAlert className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium">{lockdownWarning.message}</p>
                    <p className="text-sm opacity-80 mt-1">{lockdownWarning.reason}</p>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="page-title-input"
                  placeholder="Ex: Ma nouvelle page"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug" className="flex items-center gap-2">
                  Slug (URL) *
                  {lockdownWarning?.type === 'error' && <Lock className="h-4 w-4 text-red-500" />}
                </Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  required
                  disabled={editingPage && isProtectedRoute(editingPage.slug).protected}
                  className={lockdownWarning?.type === 'error' ? 'border-red-300' : ''}
                  data-testid="page-slug-input"
                  placeholder="Ex: /dashboard/ma-page"
                />
                <p className="text-xs text-muted-foreground">
                  L'URL de la page (doit commencer par /)
                </p>
                {!lockdownWarning && (
                  <p className="text-xs text-blue-600">
                    💡 Les routes /dashboard/nouveautes, /precommandes, /offres sont gérées par le Registry.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="menu_order">Ordre dans le menu</Label>
                <Input
                  id="menu_order"
                  type="number"
                  value={formData.menu_order}
                  onChange={(e) => setFormData({ ...formData, menu_order: parseInt(e.target.value) || 1 })}
                  min="1"
                  data-testid="page-menu-order-input"
                />
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="page-active-switch"
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">Page active</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="show_in_menu"
                    checked={formData.show_in_menu}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_in_menu: checked })}
                    data-testid="page-show-in-menu-switch"
                  />
                  <Label htmlFor="show_in_menu" className="cursor-pointer">Afficher dans le menu</Label>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  Annuler
                </Button>
                <Button type="submit" disabled={lockdownWarning?.type === 'error'}>
                  {editingPage ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <DestructiveActionDialog
          open={!!deleteTarget}
          onOpenChange={() => setDeleteTarget(null)}
          title="Supprimer la page"
          itemName={deleteTarget?.label || deleteTarget?.cmsPage?.title || ''}
          description="La page et tous ses blocs associés seront définitivement supprimés."
          cascadeWarning="Les blocs de contenu rattachés à cette page seront également supprimés."
          onConfirm={handleDelete}
        />
      </div>
    </DashboardLayout>
  );
};

export default AdminPages;
